import {
  byNumericId,
  DOMAIN_EVENT_FEED_LOOKBACK_MS,
  DOMAIN_EVENT_LOG_MAX_ROWS,
  domainEventsApi,
  isLaterSeq,
} from '#entities';
import { domainEventHistoryBackfillSettled, reportThrottled } from '#shared';

import { isReportedAuthError, recoverFromAuthFailure } from '#shared/api';
import type { AuthTokenGetter } from '#shared/api';

import {
  buildSinceUrl,
  fetchFeed,
  MAX_PAGES,
  REPORT_THROTTLE_MS,
} from './domainEventCatchup';

import type { DomainEventFeedArg, DomainEventFrame } from '#entities';
import type { AppDispatch, RootState } from '#shared';

/** The unfiltered entry the map reads: `serializeQueryArgs` keys on `view` alone. */
export const BACKFILL_ARG: DomainEventFeedArg = {};

let started = false;

/** Test seam for the once-per-page-load guard. */
export const __resetDomainEventHistoryBackfill = (): void => {
  started = false;
};

const selectLog = (state: RootState) =>
  domainEventsApi.endpoints.getDomainEvents.select(BACKFILL_ARG)(state).data;

/**
 * Walks `?sinceSeq=` forward from 0, keeping in-window rows below `floorId`.
 * `null` when the page budget ran out before a page reached the log.
 */
const collectHistoryBelow = async (
  floorId: string,
  cutoffMs: number,
  token: string,
  getToken: AuthTokenGetter,
): Promise<DomainEventFrame[] | null> => {
  const rows: DomainEventFrame[] = [];
  let cursor = '0';

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const feed = await fetchFeed(buildSinceUrl(cursor), token, getToken);
    const events = [...feed.events].sort(byNumericId);

    for (const frame of events) {
      if (!isLaterSeq(floorId, frame.id)) {
        return rows;
      }
      if (frame.ts >= cutoffMs) {
        rows.push(frame);
      }
    }

    const last = events.at(-1);
    if (!feed.capped || last === undefined) {
      return rows;
    }
    cursor = last.id;
  }

  return null;
};

/**
 * Prepends `rows` to the log in one cache write, so the grouper walks once.
 * A row at or above the log's oldest id is already held and is skipped.
 */
export const mergeDomainEventHistory =
  (rows: readonly DomainEventFrame[], nowMs: number) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const log = selectLog(getState());
    if (log === undefined) {
      return;
    }

    const floorId = log.events[0]?.id;
    const older =
      floorId === undefined
        ? [...rows]
        : rows.filter(row => isLaterSeq(floorId, row.id));
    const merged = [...older, ...log.events];
    const evicted = Math.max(0, merged.length - DOMAIN_EVENT_LOG_MAX_ROWS);
    const kept = evicted > 0 ? merged.slice(evicted) : merged;
    const cutoffMs = nowMs - DOMAIN_EVENT_FEED_LOOKBACK_MS;
    // Ascending id does not imply ascending `ts`, so the floor is a minimum.
    const historyFloorMs =
      evicted > 0
        ? kept.reduce((floor, row) => Math.min(floor, row.ts), Infinity)
        : Math.min(cutoffMs, log.historyFloorMs ?? cutoffMs);

    dispatch(
      domainEventsApi.util.patchQueryData('getDomainEvents', BACKFILL_ARG, [
        { op: 'replace', path: ['events'], value: kept },
        { op: 'replace', path: ['capped'], value: evicted > 0 },
        { op: 'replace', path: ['historyFloorMs'], value: historyFloorMs },
      ]),
    );
  };

/**
 * Loads the 48 h history the lookback's row cap cut off. Runs once per page
 * load. On any failure it merges nothing and the strip keeps its hatch.
 * Every exit settles it, so a reader waiting on the full history never hangs.
 */
export const runDomainEventHistoryBackfill = async (
  dispatch: AppDispatch,
  getToken: AuthTokenGetter,
): Promise<void> => {
  if (started) {
    return;
  }
  started = true;

  try {
    const token = await getToken();
    if (!token) {
      reportThrottled(
        'nop-events:de-backfill-no-token',
        'nop-events: domain-event history backfill skipped, no auth token',
        { throttleMs: REPORT_THROTTLE_MS, level: 'warning' },
      );
      return;
    }

    const floorId = dispatch(
      (_d, getState) => selectLog(getState())?.events[0]?.id,
    );
    if (floorId === undefined) {
      return;
    }

    const rows = await collectHistoryBelow(
      floorId,
      Date.now() - DOMAIN_EVENT_FEED_LOOKBACK_MS,
      token,
      getToken,
    );
    if (rows === null) {
      reportThrottled(
        'nop-events:de-backfill-truncated',
        'nop-events: domain-event history backfill hit the page cap before the log',
        {
          throttleMs: REPORT_THROTTLE_MS,
          level: 'warning',
          extra: { floorId, pages: MAX_PAGES },
        },
      );
      return;
    }

    dispatch(mergeDomainEventHistory(rows, Date.now()));
  } catch (error) {
    if (isReportedAuthError(error)) {
      recoverFromAuthFailure(error);
      return;
    }
    reportThrottled(
      'nop-events:de-backfill-failed',
      'nop-events: domain-event history backfill failed',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: {
          error: error instanceof Error ? error.message : String(error),
        },
      },
    );
  } finally {
    dispatch(domainEventHistoryBackfillSettled());
  }
};
